import { EditorView, Prec } from "@uiw/react-codemirror";

// Exported because /python's output panel sits under an editor: two surfaces picking their own grey would show a seam.
export const EDITOR_BACKGROUND = "var(--mantine-color-dark-6)";

const ACTIVE_LINE_TINT = "rgba(255, 255, 255, 0.04)";

// A shade under the editor, so a panel reads as a bar laid over the document; /json's status bars are the same grey.
export const PANEL_BACKGROUND = "var(--mantine-color-dark-7)";

// The primary orange at two strengths, in place of One Dark's cyan and magenta, which belong to no palette here.
const MATCH_TINT = "rgba(255, 112, 67, 0.28)";
const CURRENT_MATCH_TINT = "rgba(255, 112, 67, 0.45)";

// Mantine's `xs` controls are what a panel this compact is made of, and both these figures are that size's own.
const CONTROL_HEIGHT = "30px";
const CLOSE_SIZE = "22px";

// What @codemirror/search opens is a row of the browser's own inputs and buttons, in a font and a grey that belong
// to neither the editor nor the page around it, so it is repainted as Mantine here for the same reason the surface
// above it is: eleven editors each dressing their own Find box is eleven Find boxes that disagree. The goto-line
// dialog is the same panel with one field in it and is dressed by the same rules.
const SEARCH_PANEL = EditorView.theme({
  ".cm-panels": {
    backgroundColor: PANEL_BACKGROUND,
    color: "var(--mantine-color-text)",
    fontFamily: "var(--mantine-font-family)",
  },
  ".cm-panels.cm-panels-bottom": { borderTop: "1px solid var(--mantine-color-default-border)" },
  ".cm-panels.cm-panels-top": { borderBottom: "1px solid var(--mantine-color-default-border)" },
  // The panel is a paragraph of inline controls broken in two by a `<br>`, and it is left one: Chromium lays a `<br>`
  // out as a line break and never as a flex item, so a flex row would quietly put Replace on the end of the Find row.
  // The rhythm is then each control's own margin, which for an inline-block counts towards the line box it sits on.
  ".cm-panel.cm-search, .cm-panel.cm-dialog": {
    position: "relative",
    lineHeight: 0,
    padding: "calc(var(--mantine-spacing-xs) / 2) var(--mantine-spacing-xs)",
    // The close button is out of the flow at the corner, so the last control on a row is kept from running under it.
    paddingRight: `calc(var(--mantine-spacing-xs) * 2 + ${CLOSE_SIZE})`,
    "& input, & button, & label": {
      margin: "calc(var(--mantine-spacing-xs) / 2) var(--mantine-spacing-xs) calc(var(--mantine-spacing-xs) / 2) 0",
      verticalAlign: "middle",
    },
    // Boxed to the height of the controls beside it, `vertical-align: middle` having nothing but heights to line up.
    "& label": {
      display: "inline-flex",
      alignItems: "center",
      height: CONTROL_HEIGHT,
      gap: "calc(var(--mantine-spacing-xs) / 2)",
      fontSize: "var(--mantine-font-size-xs)",
      color: "var(--mantine-color-dimmed)",
      whiteSpace: "nowrap",
      cursor: "pointer",
      userSelect: "none",
    },
    "& label input[type=checkbox]": {
      width: "14px",
      height: "14px",
      margin: 0,
      accentColor: "var(--mantine-primary-color-filled)",
      cursor: "pointer",
    },
  },
  ".cm-textfield": {
    boxSizing: "border-box",
    width: "14rem",
    maxWidth: "100%",
    height: CONTROL_HEIGHT,
    padding: "0 var(--mantine-spacing-xs)",
    font: "inherit",
    fontSize: "var(--mantine-font-size-xs)",
    color: "var(--mantine-color-text)",
    backgroundColor: "var(--mantine-color-default)",
    border: "1px solid var(--mantine-color-default-border)",
    borderRadius: "var(--mantine-radius-default)",
    "&::placeholder": { color: "var(--mantine-color-placeholder)" },
    "&:focus": { outline: "none", borderColor: "var(--mantine-primary-color-filled)" },
  },
  // A Mantine `default` button, not a filled one: five orange buttons would outshout the page's own actions.
  ".cm-button": {
    boxSizing: "border-box",
    height: CONTROL_HEIGHT,
    padding: "0 var(--mantine-spacing-sm)",
    font: "inherit",
    fontSize: "var(--mantine-font-size-xs)",
    fontWeight: 500,
    color: "var(--mantine-color-default-color)",
    backgroundColor: "var(--mantine-color-default)",
    backgroundImage: "none",
    border: "1px solid var(--mantine-color-default-border)",
    borderRadius: "var(--mantine-radius-default)",
    cursor: "pointer",
    "&:hover": { backgroundColor: "var(--mantine-color-default-hover)" },
    "&:active": { backgroundImage: "none", backgroundColor: "var(--mantine-color-default-hover)" },
    "&:focus-visible": { outline: "2px solid var(--mantine-primary-color-filled)", outlineOffset: "2px" },
  },
  // A subtle ActionIcon at the corner, which is where the base theme already puts it and where a panel's close goes.
  ".cm-panel.cm-search [name=close], .cm-dialog-close": {
    position: "absolute",
    top: "var(--mantine-spacing-xs)",
    right: "var(--mantine-spacing-xs)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: CLOSE_SIZE,
    height: CLOSE_SIZE,
    padding: 0,
    font: "inherit",
    fontSize: "16px",
    lineHeight: 1,
    color: "var(--mantine-color-dimmed)",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "var(--mantine-radius-default)",
    cursor: "pointer",
    "&:hover": { backgroundColor: "var(--mantine-color-default-hover)", color: "var(--mantine-color-text)" },
    "&:focus-visible": { outline: "2px solid var(--mantine-primary-color-filled)", outlineOffset: "2px" },
  },
  // One Dark rings every match and tints the current one more faintly than the rest, which is the wrong way round.
  ".cm-searchMatch": { backgroundColor: MATCH_TINT, outline: "none" },
  ".cm-searchMatch.cm-searchMatch-selected": {
    backgroundColor: CURRENT_MATCH_TINT,
    outline: "1px solid var(--mantine-primary-color-filled)",
  },
});

// Every editor on the site sits in the same shell, so what One Dark is repainted onto is decided once, here.
export const EDITOR_SURFACE = [
  // The Paper already frames the editor, so the focus ring would only draw a second border inside the first.
  EditorView.theme({ "&.cm-focused": { outline: "none" } }),
  // Repaint One Dark's surface as a Mantine input; highest precedence, since One Dark is prepended to these.
  Prec.highest(EditorView.theme({
    "&": { backgroundColor: EDITOR_BACKGROUND },
    ".cm-gutters": { backgroundColor: EDITOR_BACKGROUND },
    // One Dark's blue tint reads as a band over this grey; one neutral value keeps the line whole across the seam.
    ".cm-activeLine": { backgroundColor: ACTIVE_LINE_TINT },
    ".cm-activeLineGutter": { backgroundColor: ACTIVE_LINE_TINT },
  })),
  Prec.highest(SEARCH_PANEL),
];

// The editor fills whatever wrapper holds it, at a text size the Mantine typography around it does not set for it.
export const EDITOR_STYLE = { height: "100%", fontSize: 14 };
